import React, { useState } from 'react';
import { Button, Modal, Form, Input, InputNumber, message, Spin } from 'antd';
import { useMutation } from '@apollo/client';
import { EditOutlined } from '@ant-design/icons';
import { authService } from '../../auth/authService';
import { flags } from '../../featureFlags/flags';
import { PATCH_CERTIFICATE_TEMPLATE_OVERRIDE } from '../../gql';
import RosterGroups from '../../updated/components/RosterGroups/RosterGroups';

export const EditOverride = ( { certificate, addCertificateCallback } ) => {
    const [ formController ] = Form.useForm();
    const [ isVisible, setIsVisible ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ groupSegmantationPayload ] = useState( 
        ( flags.SEGMENTATION_CLIENT_MANAGED_ATTRIBUTES_ENABLED === true || 
          flags.SEGMENTATION_CLIENT_MANAGED_ATTRIBUTES_ENABLED === 'true' )
    );
    const [ rosterEditEnabled ] = useState( flags.ROSTER_EDIT_ENABLED );

    const initialFormValues = {
        id: certificate.id,
        name: certificate.name,
        description: certificate.description !== null ? certificate.description : '',
        weight: certificate.weight,
        segments: certificate.segments.split( ',' ),
        certificateTemplateId: certificate.certificateTemplateId
    };

    const [ patchOverride ] = 
        useMutation( PATCH_CERTIFICATE_TEMPLATE_OVERRIDE );

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/
 
    const handleFormSubmission = ( values ) => {
        setIsSaving( true );
        
        patchOverride( {
            variables: {
                input: { ...values, 
                    segments: values.segments.join( ',' )
                }
            },
        } ).then( res => {
            addCertificateCallback( res.data.result );
            message.success( 'Certificate Saved' );
            setIsVisible( false );
            setIsSaving( false );
        } ).catch( err => {
            message.error( 'Error saving certificate' );
            setIsSaving( false );
        } );
    };

    const resetForm = () => {
        formController.setFields( [ 
            { 
                name: 'certificateTemplateId', 
                value: certificate.certificateTemplateId
            },
            { 
                name: 'id', 
                value: certificate.id
            },
            { 
                name: 'name', 
                value: certificate.name
            },
            { 
                name: 'description', 
                value: certificate.description !== null ? certificate.description : ''
            },
            { 
                name: 'weight', 
                value: certificate.weight
            }, 
            { 
                name: 'segments', 
                value: certificate.segments.split( ',' )
            }
        ] );
    };

    const handleCancel = () => {
        resetForm();
        setIsVisible( false );
    };

    const handleShowModal = () => { 
        resetForm();
        setIsVisible( true );
    };

    const handleSetSegments = ( segments ) => { 
        formController.setFields( [ 
            { 
                name: 'segments', 
                value: segments
            }
        ] );
    };

    return (
        <>
            <Button
                type="link" 
                icon={ <EditOutlined /> }
                onClick={ () => {
                    handleShowModal();
                } }
            />

            { isVisible &&
                <Modal
                    open={ isVisible }
                    title="Edit Override"
                    onCancel={ handleCancel }
                    footer={ [
                        isSaving ? <Spin style={ { marginRight: '10px' } } /> : null,
                        <Button 
                            key="1"
                            disabled={ isSaving }
                            onClick={ handleCancel }
                        >
                            Cancel
                        </Button>,
                        <Button
                            key="2"
                            type="primary"
                            disabled={ isSaving }
                            onClick={ () => formController.submit() }
                        >
                            Save
                        </Button>,
                    ] }
                >
                    <Form
                        form={ formController }
                        name="notification-form"
                        layout="vertical"
                        initialValues={ initialFormValues }
                        onFinish={ handleFormSubmission }
                        disabled={ isSaving }
                        scrollToFirstError
                    >   
                        { /* Name */ }
                        <Form.Item
                            name="name"
                            label="Name"
                            maxLength= { 64 }
                            rules={ [
                                {
                                    required: true,
                                    message: 'This is required to have a name.',
                                },
                                {
                                    max: 64,
                                    type: 'string',
                                    message: 'The name must be 64 characters or shorter.',
                                }
                            ] }
                        >
                            <Input />
                        </Form.Item>
                        
                        { /* Weight */ }
                        <Form.Item
                            name="weight"
                            label="Weight" 
                            nostyle
                            style={ { display: 'none' } }                                             
                        >
                            <InputNumber />
                        </Form.Item>

                        { /* Description */ }
                        <Form.Item
                            name="description"
                            label="Description"
                            maxLength= { 200 }
                            rules={ [
                                {
                                    max: 200,
                                    type: 'string',
                                    message: 'Descriptions must be 200 characters or shorter.',
                                }
                            ] }
                        >
                            <Input.TextArea
                                rows={ 4 }
                            />
                        </Form.Item>
                        
                        { /* Weight */ }
                        <Form.Item
                            name="weight"
                            label="Weight"       
                            rules={ [
                                {
                                    required: true,
                                    message: 'Weight is required to have a number',
                                }
                            ] }
                        >
                            <InputNumber />
                        </Form.Item>
                        
                        { /* Segments */ }
                        <Form.Item
                            name="segments"
                            valuePropName="segmentIds"
                            layout="horizontal"
                        >
                            <RosterGroups 
                                className="groups" 
                                auth={ authService } 
                                form={ formController } 
                                handleSelectCallback={ handleSetSegments }
                                personId={ authService.getClaims()?.[ 'person_id' ] }
                                companyId={ authService.getClaims()?.[ 'company_id' ] }
                                rosterEditButton={ rosterEditEnabled }
                                groupAttributes={ groupSegmantationPayload === true
                                    ? [ { name: 'client-managed', value: true } ]
                                    : undefined
                                }
                            />
                        </Form.Item>

                        { /* id (hidden) */ }
                        <Form.Item
                            name="id"
                            nostyle
                            style={ { display: 'none' } }
                        />

                        { /* certificateTemplateId (hidden) */ }
                        <Form.Item
                            name="certificateTemplateId"
                            nostyle
                            style={ { display: 'none' } }
                        />
                    </Form>
                </Modal>
            }
        </>        
    );
};

export default EditOverride;
